import {
  PdfGen,
  PdfUnit,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
  PdfSize,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import { GUtil } from '../../pdf/gutil';
import { Random } from '@educraft/base';
import { MercatorMap, MapRenderer, Place, MapPlaces } from '../../pdf/map';
import { ELogicoVariant, drawLogicoCircle } from '../../pdf/board/logico';
import {
  drawLcdTable,
  LcdTableData,
  LCD_TABLE_WIDTH,
  LCD_TABLE_HEIGHT,
  LCD_TOP_HEIGHT,
  LCD_MARGIN,
} from '../../pdf/board/lcdTable';

export abstract class LcdTableMapBase implements ILayoutBaseGen {
  protected mapData: MercatorMap | null = null;

  constructor(
    protected readonly pack: string,
    protected readonly places: Place[]
  ) {}

  getRequiredArchiveUrls(): string[] {
    return [this.pack];
  }

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    this.mapData = MercatorMap.loadFromArchive(opts.data.archive, this.pack);

    const page1 = pdf.addPage(true);
    const page2 = pdf.addPage(true);

    const random = opts.data?.random ?? new Random();
    const count = 3;
    const renderer = new MapRenderer(this.mapData);
    await renderer.preload(pdf);

    // Use the actual map area dimensions to ensure picked places are within the visible map area.
    // contentHeight = tableHeight - LCD_TOP_HEIGHT - LCD_MARGIN
    // mapAreaHeight = contentHeight - headerHeight (8mm)
    const contentHeight = LCD_TABLE_HEIGHT - LCD_TOP_HEIGHT - LCD_MARGIN;
    const mapAreaHeight = contentHeight - PdfUnit.FromMillimeters(8);
    const mapAreaWidth = LCD_TABLE_WIDTH - 2 * LCD_MARGIN;
    const searchRect = new PdfRect(0, 0, mapAreaWidth, mapAreaHeight);

    const items1 = renderer.pickRandomPlaces(this.places, count, searchRect, random, 2);

    const items2 = renderer.pickRandomPlaces(this.places, count, searchRect, random, 2);

    drawLcdTable(page1, random, (data: LcdTableData) => {
      this.drawMap(page1, page2, renderer, items1, data.rect1, data.rect2);
      this.drawMap(page1, page2, renderer, items2, data.rect2, data.rect1);
    });
  }

  private drawMap(
    page1: PageGen,
    page2: PageGen,
    renderer: MapRenderer,
    items: Place[],
    rect1: PdfRect,
    rect2: PdfRect
  ): void {
    const count = items.length;
    const pointSize = PdfUnit.FromMillimeters(6);
    const margin = PdfUnit.FromMillimeters(2);
    const textGap = PdfUnit.FromMillimeters(2);

    const headerHeight = PdfUnit.FromMillimeters(8);
    const r1 = new PdfRect(rect1.x, rect1.y, rect1.width, headerHeight);
    const r2 = new PdfRect(rect2.x, rect2.y, rect2.width, headerHeight);

    const names = items.map((i) => i.name);

    const totalCircleSpace = count * pointSize;
    const totalMarginSpace = margin * 2;
    const totalGapSpace = count * textGap;
    const availableForText = r1.width - totalCircleSpace - totalMarginSpace - totalGapSpace;

    const naturalWidths = names.map((name) => {
      const size = page1.measureString(name, GUtil.baseFont);
      return size.width;
    });

    const totalNaturalWidth = naturalWidths.reduce((a, b) => a + b, 0);

    let scaleFactor = 1;
    if (totalNaturalWidth > availableForText && totalNaturalWidth > 0) {
      scaleFactor = availableForText / totalNaturalWidth;
    }

    const scaledWidths = naturalWidths.map((w) => w * scaleFactor);

    const font = new PdfFont(PdfFont.FONT_NAME, GUtil.baseFont.size * scaleFactor);

    const mapRect1 = new PdfRect(
      rect1.x,
      rect1.y + headerHeight,
      rect1.width,
      rect1.height - headerHeight
    );
    const mapRect2 = new PdfRect(
      rect2.x,
      rect2.y + headerHeight,
      rect2.width,
      rect2.height - headerHeight
    );

    const gpsPoints = items.map((i) => i.gps);
    const points1 = renderer.pointFromGps(mapRect1, gpsPoints);
    const points2 = renderer.pointFromGps(mapRect2, gpsPoints);

    renderer.drawMap(page1, mapRect1);
    renderer.drawMap(page2, mapRect2);

    let currentX1 = r1.x + margin;
    let currentX2 = r2.x + margin;

    for (let i = 0; i < count; i++) {
      const textWidth = scaledWidths[i];
      const circleRect1 = new PdfRect(
        currentX1,
        r1.y + (r1.height - pointSize) / 2,
        pointSize,
        pointSize
      );
      const circleRect2 = new PdfRect(
        currentX2,
        r2.y + (r2.height - pointSize) / 2,
        pointSize,
        pointSize
      );

      const textRect1 = new PdfRect(currentX1 + pointSize + textGap, r1.y, textWidth, r1.height);
      const textRect2 = new PdfRect(currentX2 + pointSize + textGap, r2.y, textWidth, r2.height);

      const label = items[i].name;

      page1.drawString(label, font, PdfBrush.Black, null, textRect1, ETextAlign.CenterLeft);
      page2.drawString(label, font, PdfBrush.Black, null, textRect2, ETextAlign.CenterLeft);

      drawLogicoCircle(page1, ELogicoVariant.Primo, i, circleRect1);
      drawLogicoCircle(page2, ELogicoVariant.Primo, i, circleRect2);

      // const mapCircleRect1 = new PdfRect(points1[i].x - pointSize / 2, points1[i].y - pointSize / 2, pointSize, pointSize);
      // drawLogicoCircle(page1, ELogicoVariant.Primo, i, mapCircleRect1);

      const mapCircleRect2 = new PdfRect(
        points2[i].x - pointSize / 2,
        points2[i].y - pointSize / 2,
        pointSize,
        pointSize
      );
      drawLogicoCircle(page2, ELogicoVariant.Primo, i, mapCircleRect2);

      currentX1 += pointSize + textGap + textWidth + textGap;
      currentX2 += pointSize + textGap + textWidth + textGap;
    }
  }
}

export class LCD_Tabulka_Mapa__CR__Kraje extends LcdTableMapBase {
  constructor() {
    super('map/map-cs.zip', MapPlaces.kraje());
  }
}

export class LCD_Tabulka_Mapa__CR__KrajskaMesta extends LcdTableMapBase {
  constructor() {
    super('map/map-cs.zip', MapPlaces.krajskaMesta());
  }
}

export class LCD_Tabulka_Mapa__Evropa__HlavniMesta extends LcdTableMapBase {
  constructor() {
    super('map/map-europe.zip', MapPlaces.hlavniEvropskaMesta());
  }
}

export class LCD_Tabulka_Mapa__Svet__Staty extends LcdTableMapBase {
  constructor() {
    super('map/map-world.zip', MapPlaces.staty());
  }
}
